﻿import { useState } from 'react';
import { CheckIcon } from 'lucide-react';

import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from 'shared-ui';

import { useClient } from '@/clients/useClient';
import { getInitials } from '@/utils/avatarUtils';
import { getThemedLogoSrcs } from '@/utils/logoUtils';

/**
 * The Network Explorer entry at the top of the switcher. Entering the explorer is a
 * navigation, never a client switch — the selected client stays as it was underneath.
 */
export interface NetworkExplorerSwitcherEntry {
    /** Whether the user may enter the explorer (the row is absent otherwise, never disabled). */
    available: boolean;
    /** Whether the explorer is the current view (the row shows the check, no client row does). */
    active: boolean;
    onSelect: () => void;
}

interface ClientSwitcherDropdownProps {
    /** Display name of the currently selected client — shown in the avatar fallback. */
    clientName: string;
    /** Logo URL of the currently selected client. Falls back to initials when absent. */
    clientLogoSrc?: string | null;
    explorer?: NetworkExplorerSwitcherEntry;
    /**
     * Fired after a client row is chosen, whether or not it was already the selected client,
     * so the shell can leave the explorer for that client's product.
     */
    onClientSelected?: (clientId: string) => void;
}

/**
 * Client-switcher button + dropdown.
 *
 * Self-contained: reads the client list and switch action from `ClientContext`
 * via `useClient()`. The trigger renders the selected client's logo (or initials)
 * and opens a compact "SWITCH NETWORK" menu listing all accessible clients, led by
 * the Network Explorer entry for users who may enter it.
 */
export function ClientSwitcherDropdown({
    clientName,
    clientLogoSrc,
    explorer,
    onClientSelected,
}: ClientSwitcherDropdownProps) {
    const { clients, selectedClient, isLoading, isError, switchClient, isOriginUser } = useClient();
    const explorerActive = explorer?.active ?? false;

    // Track the URLs that most recently failed to load, so we can degrade
    // gracefully: themed variants → base logo → initials. Comparing against
    // clientLogoSrc means a switch to a different client retries automatically —
    // no useEffect reset required.
    const [failedSrc, setFailedSrc] = useState<string | null>(null);
    const [variantsFailedFor, setVariantsFailedFor] = useState<string | null>(null);

    // Each client ships a light-theme (dark ink) and dark-theme (white ink) logo
    // as `-light`/`-dark` siblings of its base logo path (see utils/logoUtils),
    // so white brand logos stay legible on the light theme and vice-versa.
    const variants = getThemedLogoSrcs(clientLogoSrc);
    const useVariants = variants !== null && variantsFailedFor !== clientLogoSrc;
    const useBaseLogo = !!clientLogoSrc && !useVariants && failedSrc !== clientLogoSrc;

    const logoDiv = (
        /*
         * A plain div gives full control over aspect ratio so landscape logos
         * (e.g. Severn Trent) display at natural width without being squashed
         * into a square. Avatar is intentionally not used here because its
         * AvatarImage has `aspect-square object-cover` baked in.
         */
        <div className="border-border bg-muted flex size-14 items-center justify-center overflow-hidden rounded-md border p-1">
            {explorerActive ? (
                /* The explorer belongs to no client, so the tile names the company instead of a logo. */
                <span className="text-foreground text-xs font-semibold tracking-wider select-none">ORIGIN</span>
            ) : useVariants ? (
                /*
                 * Theme swap mirrors the AppSidebar/UpsellDialog pattern: both variants
                 * are rendered, CSS shows the one matching the active theme. A load
                 * failure of either falls back to the single base logo below.
                 */
                <>
                    <img
                        src={variants.light}
                        alt={clientName}
                        className="block h-full w-full object-contain dark:hidden"
                        onError={() => {
                            setVariantsFailedFor(clientLogoSrc ?? null);
                        }}
                    />
                    <img
                        src={variants.dark}
                        alt={clientName}
                        className="hidden h-full w-full object-contain dark:block"
                        onError={() => {
                            setVariantsFailedFor(clientLogoSrc ?? null);
                        }}
                    />
                </>
            ) : useBaseLogo ? (
                <img
                    src={clientLogoSrc}
                    alt={clientName}
                    className="h-full w-full object-contain"
                    onError={() => {
                        // useBaseLogo narrows clientLogoSrc to a non-null string.
                        setFailedSrc(clientLogoSrc);
                    }}
                />
            ) : (
                <span className="text-foreground text-xs font-semibold select-none">{getInitials(clientName)}</span>
            )}
        </div>
    );

    if (!isOriginUser) return logoDiv;

    return (
        <Tooltip>
            <DropdownMenu>
                {/*
                 * Base UI uses the `render` prop to customise the trigger element.
                 * TooltipTrigger is nested inside DropdownMenuTrigger's render prop so
                 * both sets of event handlers are merged onto the same <button> element.
                 */}
                <DropdownMenuTrigger
                    render={
                        <TooltipTrigger
                            render={
                                <Button
                                    variant="ghost"
                                    className="h-auto w-auto cursor-pointer p-0 hover:bg-transparent focus-visible:border-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                                    aria-label="Switch network"
                                />
                            }
                        />
                    }
                >
                    {logoDiv}
                </DropdownMenuTrigger>

                {/*
                 * w-[216px] overrides the default w-(--anchor-width) so the menu is
                 * wider than the small avatar trigger. twMerge resolves the conflict.
                 */}
                <DropdownMenuContent
                    side="bottom"
                    align="end"
                    sideOffset={8}
                    // client-switcher-menu: hover previews the selected look (see index.css)
                    className="client-switcher-menu w-65 py-2"
                >
                    {/* DropdownMenuLabel requires a DropdownMenuGroup parent (Base UI constraint) */}
                    <DropdownMenuGroup>
                        <DropdownMenuLabel className="text-foreground px-3 py-1 text-sm leading-5 font-black uppercase">
                            Switch Network
                        </DropdownMenuLabel>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />

                    {explorer?.available && (
                        <>
                            <DropdownMenuItem
                                className={
                                    explorerActive
                                        ? 'bg-secondary focus:bg-secondary cursor-pointer rounded-sm px-3 py-1'
                                        : 'cursor-pointer rounded-sm px-3 py-1'
                                }
                                onClick={() => {
                                    if (!explorerActive) explorer.onSelect();
                                }}
                            >
                                <span className="flex w-4 shrink-0 items-center justify-center">
                                    {explorerActive && (
                                        <CheckIcon
                                            className="text-secondary-foreground size-3.5"
                                            aria-label="Current network"
                                        />
                                    )}
                                </span>
                                <span
                                    className={
                                        explorerActive
                                            ? 'text-secondary-foreground truncate'
                                            : 'text-foreground truncate'
                                    }
                                >
                                    Network Explorer
                                </span>
                            </DropdownMenuItem>
                            {/* The explorer is a mode, not a client: a dashed rule, inset a little
                                from both edges, sets it apart from the client rows below. Drawn as a
                                border so the dash pattern comes from the stylesheet, not an image. */}
                            <DropdownMenuSeparator
                                data-testid="network-explorer-divider"
                                className="border-border mx-1.5 h-0 border-t border-dashed bg-transparent"
                            />
                        </>
                    )}

                    {isLoading && <p className="text-muted-foreground px-3 py-1.5 text-sm">Loading…</p>}

                    {isError && !isLoading && (
                        <p className="text-destructive px-3 py-1.5 text-sm">Failed to load. Try again.</p>
                    )}

                    {!isLoading && !isError && clients.length === 0 && (
                        <p className="text-muted-foreground px-3 py-1.5 text-sm">No networks available.</p>
                    )}

                    {!isLoading && !isError && clients.length > 0 && (
                        <div className="max-h-105 overflow-y-auto">
                            {clients.map((client) => {
                                // While the explorer is the view, no client row is "current" — the
                                // selection underneath is real but not what the user is looking at.
                                const isSelected = !explorerActive && selectedClient?.id === client.id;
                                return (
                                    <DropdownMenuItem
                                        key={client.id}
                                        className={
                                            isSelected
                                                ? 'bg-secondary focus:bg-secondary cursor-pointer rounded-sm px-3 py-1'
                                                : 'cursor-pointer rounded-sm px-3 py-1'
                                        }
                                        onClick={() => {
                                            // Switch only when it is genuinely a different client: inside
                                            // the explorer the underlying selection may already be this one.
                                            if (selectedClient?.id !== client.id) switchClient(client.id);
                                            onClientSelected?.(client.id);
                                        }}
                                    >
                                        {/*
                                         * Fixed-width left slot keeps text aligned whether or
                                         * not the checkmark is present.
                                         */}
                                        <span className="flex w-4 shrink-0 items-center justify-center">
                                            {isSelected && (
                                                <CheckIcon
                                                    className="text-secondary-foreground size-3.5"
                                                    aria-label="Current network"
                                                />
                                            )}
                                        </span>
                                        <span
                                            className={
                                                isSelected
                                                    ? 'text-secondary-foreground truncate'
                                                    : 'text-foreground truncate'
                                            }
                                        >
                                            {client.name}
                                        </span>
                                    </DropdownMenuItem>
                                );
                            })}
                        </div>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
            <TooltipContent side="bottom" align="center">
                Switch Network
            </TooltipContent>
        </Tooltip>
    );
}
